/*! Copyright © 2026 Kontextlab GmbH. All rights reserved. */

// Reading order and directional movement for the map's points.
//
// The points are absolutely positioned, and their order in the document is
// the order they were authored in, which says nothing about where they sit
// on screen. Everything here answers the two questions that follow from
// that: in what order should the points be read, and which point is "to the
// left" of this one.
//
// Pure functions over map data. The DOM side owns the elements.

/** Height of one row when grouping points into reading lines, in map units. */
const DEFAULT_ROW_HEIGHT = 100;

/** Half-angle of the cone an arrow key searches within, in radians. */
const DIRECTION_CONE = Math.PI / 4;

const DIRECTION_VECTORS = {
    ArrowRight: [1, 0],
    ArrowLeft: [-1, 0],
    ArrowUp: [0, -1],
    ArrowDown: [0, 1]
};

function centerOf(node) {
    const center = node?.geometry?.center;
    return {
        x: Number(center?.x) || 0,
        y: Number(center?.y) || 0
    };
}

export const Keyboard = {
    DIRECTION_VECTORS,

    /**
     * Report whether a point is shown at the given zoom level.
     *
     * @param {object} node - Map node data.
     * @param {number} level - Current zoom level.
     * @returns {boolean}
     */
    visibleAtLevel(node, level) {
        const min = node?.visibility?.min_level;
        const max = node?.visibility?.max_level;
        if (min != null && level < min) return false;
        if (max != null && level > max) return false;
        return true;
    },

    /**
     * Order points for sequential reading.
     *
     * Prefers the order the author defined through the previous/next chain
     * that already drives the popup navigation. Falls back to reading the
     * map in lines, top to bottom and then across, which is a defensible
     * guess rather than authored intent.
     *
     * @param {object[]} nodes - Map node data.
     * @param {object} [options]
     * @param {number} [options.rowHeight] - Line height for the fallback.
     * @param {boolean} [options.rtl] - Read each line right to left.
     * @returns {object[]} The same nodes, ordered.
     */
    readingOrder(nodes, options = {}) {
        const list = (nodes || []).filter(Boolean);
        if (list.length < 2) {
            return list.slice();
        }
        const rowHeight = options.rowHeight || DEFAULT_ROW_HEIGHT;
        const byId = new Map(list.map((node) => [String(node.id), node]));

        // Group into lines by proximity rather than by rounding to a fixed
        // grid: a grid puts its boundaries at arbitrary heights, so two
        // points a few pixels apart can land in different lines while two
        // most of a row apart share one.
        const byHeight = list.slice().sort((a, b) => {
            const dy = centerOf(a).y - centerOf(b).y;
            if (dy !== 0) return dy;
            return centerOf(a).x - centerOf(b).x;
        });

        const rows = [];
        let rowTop = null;
        for (const node of byHeight) {
            const { y } = centerOf(node);
            if (rowTop === null || y - rowTop > rowHeight / 2) {
                rows.push([]);
                rowTop = y;
            }
            rows[rows.length - 1].push(node);
        }

        const geometric = rows.flatMap((row) =>
            row.sort((a, b) => {
                const dx = centerOf(a).x - centerOf(b).x;
                if (dx !== 0) return options.rtl ? -dx : dx;
                return String(a.id).localeCompare(String(b.id));
            })
        );

        const hasChain = list.some(
            (node) =>
                node.content?.navigation?.next_id != null ||
                node.content?.navigation?.previous_id != null
        );
        if (!hasChain) {
            return geometric;
        }

        // Start where the chain starts, or at the first point in reading
        // order if the author linked it into a ring.
        const heads = list.filter(
            (node) => node.content?.navigation?.previous_id == null
        );
        const ordered = [];
        const seen = new Set();
        const walk = (node) => {
            let current = node;
            // A chain the author closed into a loop would otherwise spin
            // here forever.
            while (current && !seen.has(String(current.id))) {
                seen.add(String(current.id));
                ordered.push(current);
                const nextId = current.content?.navigation?.next_id;
                current = nextId == null ? null : byId.get(String(nextId));
            }
        };
        for (const head of heads.length ? heads : [geometric[0]]) {
            walk(head);
        }
        // Anything the chain never reached still has to be readable.
        for (const node of geometric) {
            if (!seen.has(String(node.id))) {
                seen.add(String(node.id));
                ordered.push(node);
            }
        }
        return ordered;
    },

    /**
     * Find the nearest point in a direction.
     *
     * Only considers points inside a 45-degree cone, so pressing right moves
     * right rather than to whatever happens to be closest.
     *
     * @param {object} from - The point focus is on.
     * @param {object[]} candidates - Points that could receive focus.
     * @param {string} key - An arrow key name.
     * @returns {object|null}
     */
    nearestInDirection(from, candidates, key) {
        const vector = DIRECTION_VECTORS[key];
        if (!vector || !from) return null;
        const origin = centerOf(from);
        let best = null;
        let bestScore = Number.POSITIVE_INFINITY;

        for (const candidate of candidates || []) {
            if (candidate === from) continue;
            const point = centerOf(candidate);
            const dx = point.x - origin.x;
            const dy = point.y - origin.y;
            const distance = Math.hypot(dx, dy);
            if (distance === 0) continue;

            const along = (dx * vector[0] + dy * vector[1]) / distance;
            if (along < Math.cos(DIRECTION_CONE)) continue;

            // Prefer points straight ahead over ones off to the side.
            const score = distance / Math.max(along, 0.0001);
            if (score < bestScore) {
                bestScore = score;
                best = candidate;
            }
        }
        return best;
    },

    /**
     * Find the point nearest the middle of the shown area.
     *
     * For moving the tab stop after a zoom has dropped the point it was
     * on: the reader is left on something they can see, and the map does
     * not have to move to put it there.
     *
     * @param {object[]} candidates - Points that could receive focus.
     * @param {{x: number, y: number, width: number, height: number}} view -
     *   The shown area, in map units.
     * @returns {object|null}
     */
    nearestToView(candidates, view) {
        const list = (candidates || []).filter(Boolean);
        if (!list.length) return null;
        const bounds = [view?.x, view?.y, view?.width, view?.height];
        if (!bounds.every((value) => Number.isFinite(value))) {
            return list[0];
        }
        const centerX = view.x + view.width / 2;
        const centerY = view.y + view.height / 2;
        let best = null;
        let bestDistance = Number.POSITIVE_INFINITY;

        for (const candidate of list) {
            const point = centerOf(candidate);
            const distance = Math.hypot(
                point.x - centerX,
                point.y - centerY
            );
            if (distance < bestDistance) {
                bestDistance = distance;
                best = candidate;
            }
        }
        return best;
    }
};

export default Keyboard;
